import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common'
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'

import { MsalService } from '@azure/msal-angular'
import { TranslatePipe } from '@ngx-translate/core'
import { MenuItem } from 'primeng/api'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { BadgeModule } from 'primeng/badge'
import { ButtonModule } from 'primeng/button'
import { DividerModule } from 'primeng/divider'
import { Drawer } from 'primeng/drawer'
import { Menu, MenuModule } from 'primeng/menu'
import { MenubarModule } from 'primeng/menubar'
import { RippleModule } from 'primeng/ripple'
import { Subject } from 'rxjs'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { SearchComponent } from 'app/_shared/components/search/search.component'
import { StoreStatusBannerComponent } from 'app/_shared/components/store-status-banner/store-status-banner.component'
import { isBaseAiEnabled } from 'app/_shared/helpers/base-points.helpers'
import { BasePointsCurrency } from 'app/_shared/interfaces/BasePoints'
import { Menu as CustomMenu } from 'app/_shared/interfaces/config'
import { BasePointsPipe } from 'app/_shared/pipes/base-points.pipe'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { BasePointsService } from 'app/_shared/services/base-points.service'
import { LayoutService } from 'app/_shared/services/layout.service'
import { LogoService } from 'app/_shared/services/logo.service'
import { OktaAuthService } from 'app/_shared/services/okta-auth.service'
import { AppConfigService } from 'app/app-config.service'
import { CartStore } from 'app/store/cart.store'
import { ConfigStore } from 'app/store/config.store'
import { GiftCardStore } from 'app/store/gift-card.store'
import { UserStore } from 'app/store/user.store'

@Component({
  selector: 'app-navbar',
  imports: [
    ReactiveFormsModule,
    AutoCompleteModule,
    RouterLink,
    RouterLinkActive,
    BadgeModule,
    NgClass,
    ButtonModule,
    RippleModule,
    ButtonComponent,
    MenuModule,
    TranslatePipe,
    NgOptimizedImage,
    StoreStatusBannerComponent,
    MenubarModule,
    NgTemplateOutlet,
    DividerModule,
    UsdPipe,
    PricePipe,
    SearchComponent,
    Drawer,
    BasePointsPipe
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  cartStore = inject(CartStore)
  userStore = inject(UserStore)
  configStore = inject(ConfigStore)
  appConfigService = inject(AppConfigService)
  private router = inject(Router)
  msalService = inject(MsalService)
  private oktaAuthService = inject(OktaAuthService)
  private giftCardStore = inject(GiftCardStore)
  private basePointsService = inject(BasePointsService)
  readonly logo = inject(LogoService)
  private layout = inject(LayoutService)
  disabledPrices: boolean = false

  menubarDt = {
    root: {
      padding: '8px'
    },
    baseItem: {
      padding: '0.75rem 1rem'
    },
    item: {
      padding: '0.75rem 1rem',
      focusBackground: '#e9ecef',
      focusColor: '#495057'
    }
  }

  menuDt = {
    root: {
      padding: '12px'
    },
    item: {
      padding: '1rem 1.25rem',
      focusBackground: '#e9ecef',
      focusColor: '#495057'
    }
  }

  giftCardBalance = computed(() => this.giftCardStore.navbarBalance())
  basePointsEnabled = isBaseAiEnabled(this.appConfigService.getAppConfig?.base_ai_enabled)
  basePointsBalance = this.basePointsService.balance
  basePointsCurrency = computed<BasePointsCurrency>(() => {
    const balance = this.basePointsBalance()
    const config = this.appConfigService.getAppConfig

    return {
      currency_long_name:
        balance?.currency_long_name ?? config?.currency_long_name ?? 'Base points',
      currency_short_name: balance?.currency_short_name ?? config?.currency_short_name ?? 'pts',
      currency_prefix: balance?.currency_prefix ?? config?.currency_prefix ?? false
    }
  })
  isGifting = computed(() => this.cartStore.giftingToken())
  cartItemsLength = computed(() =>
    this.cartStore.items().reduce((total, item) => total + item.quantity, 0)
  )
  userName = computed(() => this.userStore.user()?.first_name)
  menuItems = computed(() => {
    const configMenuItems = this.configStore.config()?.menu ?? []
    return [...configMenuItems]
  })

  menuItemsForPrimeNg = computed<MenuItem[]>(() => {
    return this.buildMenuItems(this.menuItems(), true)
  })

  menuItemsBar = computed(() => {
    const configMenuItems = this.configStore.config()?.menu ?? []
    return this.buildMenuItems(configMenuItems)
  })
  logoPosition = computed(() => {
    return this.configStore.config()?.settings?.logoPosition ?? 'left'
  })
  protected readonly headerBackgroundColor = computed(
    () => this.configStore.config().settings?.headerBackgroundColor?.trim() || null
  )
  protected readonly menuBackgroundColor = computed(
    () => this.configStore.config().settings?.menuBackgroundColor?.trim() || null
  )
  protected readonly menuTextColor = computed(
    () => this.configStore.config().settings?.menuTextColor?.trim() || null
  )

  destroyed = new Subject<void>()
  isMobile = this.layout.isMobile
  searchForm = new FormGroup({
    search: new FormControl('')
  })
  mobileMenuVisible = false
  mobileSearchVisible = false

  @ViewChild('userMenu') userMenu: Menu
  userMenuItems: MenuItem[] = [
    {
      label: 'My Account',
      icon: 'pi pi-user',
      routerLink: '/my-account'
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    }
  ]

  constructor() {
    effect(() => {
      this.configStore.config()
    })
  }

  ngOnInit() {
    this.disabledPrices = this.appConfigService.getAppConfig?.disabled_prices
  }

  ngOnDestroy() {
    this.destroyed.next()
    this.destroyed.complete()
  }

  buildMenuItems(menus: CustomMenu[], mobile = false) {
    return menus
      .map(menu => {
        const link = menu.item ? '/' + menu.item.url : menu.customUrl
        return {
          label: menu.label,
          routerLink: link,
          href: menu.customUrl,
          visible: menu.visible,
          items:
            menu.children && menu.children.length > 0
              ? this.buildMenuItems(menu.children, mobile)
              : undefined,
          ...(mobile
            ? {
                command: () => {
                  this.router.navigate([link])
                  this.mobileMenuVisible = false
                }
              }
            : {})
        }
      })
      .filter(item => item.visible)
  }

  // Show the menu on mouse enter
  onMouseEnter(event: Event) {
    this.userMenu.show(event)
  }

  // Hide the menu on mouse leave
  onMouseLeave() {
    // this.userMenu.hide()
  }

  logout() {
    // Always clear the local user state.
    this.userStore.logoutUser()

    const ssoProvider = this.appConfigService.getAppConfig?.sso?.provider

    if (ssoProvider === 'okta' && this.oktaAuthService.hasOktaSession()) {
      // Okta SSO logout
      this.oktaAuthService.logout(window.location.origin).catch(error => {
        console.error('Okta logout error:', error)
      })
    } else {
      // Check if a Microsoft account is active (i.e. the user logged in with SSO)
      const activeAccount = this.msalService.instance.getActiveAccount()
      if (activeAccount) {
        this.msalService.logoutRedirect({
          account: activeAccount,
          postLogoutRedirectUri: window.location.origin
        })
      } else {
        const { private_store } = this.appConfigService.getAppConfig || {}
        if (private_store) {
          this.router.navigate(['/login'])
        } else {
          this.router.navigate(['/'])
        }
      }
    }
  }
}
